// askSmart-ui/src/pages/TOSP/tabs/BudgetTab.tsx
import React, { useEffect, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import AddBudgetModal from "../components/AddBudgetModal";
import BudgetHistoryModal from "../components/BudgetHistoryModal";
import BudgetSetupWizard, { BudgetSetup } from "../components/BudgetSetupWizard";
import BudgetPublishModal from "../components/BudgetPublishModal";
import ExpenseBudgetPanel from "../components/ExpenseBudgetPanel";
import RevenueBudgetPanel from "../components/RevenueBudgetPanel";
import {
  prunedExpenseEdits,
  effectiveLines,
  managementFeeLine,
  resolveCategorySplits,
} from "../budgetAllocation";
import {
  buildPublishedSnapshot,
  camRecoveryLine,
  PublishIncompleteError,
  statementBasis,
} from "../budgetPublish";
import { statementRatesFor, tenantExpenseYear } from "../portal/tenantExpenses";
import {
  CAM_RECOVERY_ACCOUNT,
  CAM_RECOVERY_KEY,
  MONTH_LABELS,
  projectRevenue,
} from "../revenueBudget";
import { useStoredSales } from "../portal/tenantSales";
import {
  BudgetHistoryStepKey,
  deriveBudgetStatus,
  nextBudgetAction,
  setBudgetStep,
} from "../budgetHistory";
import { useAuthentication } from "../../../context/AuthContext";
import { useTospChrome } from "../TospChromeContext";
import { useTospData } from "../TospDataContext";
import {
  BudgetPublishedSnapshot,
  BudgetRevenueLine,
  BudgetYear,
  budgetLabel,
  MANAGEMENT_FEE_ACCOUNT,
  newId,
} from "../types";
import { useGridSort } from "../useGridSort";

const LIST_COLUMNS = "2fr 0.8fr 1.4fr 1.5fr";

const money2 = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

type SortCol = "name" | "year" | "status";

const SORT_COLUMNS: { col: SortCol; label: string }[] = [
  { col: "name", label: "Budget Name" },
  { col: "year", label: "Year" },
  { col: "status", label: "Status" },
];

// Budget — a list of budgets, the same shape as every other management grid in
// the module. Opening one drills into its workspace: the projected revenue,
// then the expense side once the QuickBooks round trip is done.
//
// Several budgets can share a year (three drafts of 2027 get worked up and the
// losers deleted), so the NAME identifies a budget, not the year.
export default function BudgetTab() {
  const {
    budgets,
    saveBudget,
    deleteBudget,
    unlockBudget,
    configuration,
    units,
    tenants,
    unitById,
    unitLabel,
    taxRolls,
  } = useTospData();
  // Read here only to build the snapshot when the budget is published — the
  // panel does its own projecting until then.
  const { sales } = useStoredSales();
  // The budget being worked in. Null shows the list.
  const [openId, setOpenId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<BudgetYear | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [deleting, setDeleting] = useState<BudgetYear | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  /** The row whose secondary actions are showing — at most one at a time. */
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // Closes when the admin clicks away from the actions, as the same control
  // does in SMRT Tasks. Bound only while a row is open, so there is no global
  // listener running for nothing.
  useEffect(() => {
    if (!menuFor) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest(".tosp-actions")) setMenuFor(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [menuFor]);
  /** The budget whose Broadcast is being switched ON, pending confirmation. */
  const [broadcasting, setBroadcasting] = useState<BudgetYear | null>(null);
  /**
   * Why a Broadcast was refused, shown under the list.
   *
   * One budget per year: the statement has a single tab for 2027, and two
   * drafts of 2027 broadcasting at once would leave it unclear which one the
   * tab is showing. Turning a second one on is refused rather than switching
   * the first off, so nothing an admin set up is undone behind their back.
   * The server refuses it as well — this is the courtesy, not the control.
   */
  const [broadcastBlocked, setBroadcastBlocked] = useState<string | null>(null);
  /** Momentary, so the Copy for QBO button can say it worked. */
  const [qboCopied, setQboCopied] = useState(false);
  /** Which end of publication the dialog is showing, if any. */
  const [publishMode, setPublishMode] = useState<"publish" | "unlock" | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  /** The snapshot a confirmed publish will write, built when the dialog opens. */
  const [pending, setPending] = useState<BudgetPublishedSnapshot | null>(null);
  /**
   * Revenue adjustments typed but not yet saved. Null means nothing is staged,
   * which is what makes Save Changes go quiet — an empty object is different:
   * it means every adjustment has been cleared and that is itself a change.
   */
  const [draftOverrides, setDraftOverrides] = useState<Record<string, number> | null>(null);
  /** The same, for who carries which expense. Staged and saved together. */
  const [draftExpenseOverrides, setDraftExpenseOverrides] = useState<Record<
    string,
    number
  > | null>(null);

  const { setChromeHidden } = useTospChrome();
  const { user } = useAuthentication();

  // Stamped onto every progression step, so the history says who as well as when.
  const actor = { by: user?.sub, byName: user?.name || user?.email || "" };

  const years = budgets.map((b) => b.year);
  const openBudget = budgets.find((b) => b.id === openId) || null;
  const historyBudget = budgets.find((b) => b.id === historyFor) || null;

  // A budget open for work takes the whole page — the module's tab row is
  // about switching sections, which isn't what this screen is for. The cleanup
  // puts it back, including if this tab unmounts with a budget still open.
  useEffect(() => {
    setChromeHidden(!!openId);
    return () => setChromeHidden(false);
  }, [openId, setChromeHidden]);

  // Staged edits belong to the budget they were typed into. Closing one, or
  // opening another, drops them rather than carrying them across — an
  // adjustment landing on a different budget would be silent and wrong.
  useEffect(() => {
    setDraftOverrides(null);
    setDraftExpenseOverrides(null);
  }, [openId]);

  // A budget from before the progression existed has no "built" step, which
  // would leave it with no next action and no way to get one. Record it on
  // first open — the timestamp is when it was noticed rather than when it was
  // really created, which is the best that can be recovered.
  const openHasCreated = !!budgets.find((b) => b.id === openId)?.history?.created;
  useEffect(() => {
    if (openId && !openHasCreated) advance(openId, "created");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, openHasCreated]);

  const { sortButton, sortRows } = useGridSort<SortCol, BudgetYear>("name", (budget, col) => {
    switch (col) {
      case "name":
        return budgetLabel(budget);
      case "year":
        return budget.year;
      case "status":
        return deriveBudgetStatus(budget.history);
      default:
        return "";
    }
  });

  // One budget, one document. Every change here writes that document whole —
  // its lines, splits, history and revenue adjustments are one piece of work —
  // and the save carries the ETag it was read with, so two admins in the same
  // budget get a refusal rather than one quietly losing their afternoon.
  //
  // Failures surface through the provider's banner; nothing is swallowed here.
  const patch = (id: string, changes: Partial<BudgetYear>) => {
    const budget = budgets.find((b) => b.id === id);
    if (!budget) return;
    saveBudget({ ...budget, ...changes }).catch(() => undefined);
  };

  /**
   * Why this budget may not be deleted, or null.
   *
   * THE SERVER REFUSES BOTH OF THESE TOO. Said here as well so an admin reads
   * the reason on the button rather than after pressing it: a broadcasting
   * budget is on tenants' screens, and a published one holds the snapshot
   * those screens were billed from. Both come off deliberately — Broadcast
   * switched off, then unlocked with a reason — before the document can go.
   */
  const deleteBlocked = (budget: BudgetYear): string | null => {
    if (budget.broadcastExpenses) {
      return "Broadcasting to tenants. Turn Broadcast off before deleting it.";
    }
    if (budget.history?.published) {
      return "Published and locked. Unlock it, with a reason, before deleting it.";
    }
    return null;
  };

  /**
   * Switches Broadcast for one budget.
   *
   * Turning it ON is confirmed first — it puts a year in front of every
   * tenant — and refused outright where another budget already broadcasts
   * that year, which the server enforces as well. Turning it OFF needs no
   * ceremony: taking something back is not the risky direction.
   */
  const setBroadcast = (budget: BudgetYear, on: boolean) => {
    setBroadcasting(null);
    if (on) {
      const already = budgets.find(
        (b) => b.id !== budget.id && b.year === budget.year && b.broadcastExpenses,
      );
      if (already) {
        setBroadcastBlocked(
          `Another budget for ${budget.year} is already being broadcast: ` +
            `${budgetLabel(already)}. Turn Broadcast off there before broadcasting this one.`,
        );
        return;
      }
    }
    setBroadcastBlocked(null);
    patch(budget.id, { broadcastExpenses: on });
  };

  const addOrRenameBudget = (year: number, name: string) => {
    if (editing) {
      // Moving a broadcasting budget onto a year another budget already
      // broadcasts would put two on one tab — so it arrives switched off.
      const clash =
        editing.broadcastExpenses &&
        budgets.some((b) => b.id !== editing.id && b.year === year && b.broadcastExpenses);
      patch(editing.id, { year, name, ...(clash ? { broadcastExpenses: false } : {}) });
      setEditing(null);
      return;
    }
    const created: BudgetYear = {
      id: newId("bgt"),
      year,
      name,
      // Built the moment it exists, and stamped with who did it — the first
      // step is never something the admin has to go and tick.
      history: setBudgetStep(undefined, "created", true, actor),
      lines: [],
      splits: [],
    };
    // Opened only once it has landed: a budget that failed to save must not be
    // sitting open as though it existed.
    saveBudget(created)
      .then(() => {
        setAddOpen(false);
        setOpenId(created.id);
      })
      .catch(() => undefined);
  };

  /**
   * One budget's CAM recovery as the 41001 revenue line.
   *
   * Derived, never stored: it is a function of the budget's own expense lines,
   * their categories and the splits, all of which are already on the document.
   * A stored copy could disagree with them, and did — a category reassigned or
   * a lease corrected left the recovery describing an arrangement that no
   * longer existed until somebody pressed a button again.
   *
   * Takes the budget rather than reading the open one, because the freeze below
   * can be asked to capture a budget the admin isn't currently inside.
   */
  const camRecoveryFor = (budget: BudgetYear): BudgetRevenueLine | undefined =>
    camRecoveryLine(
      budget,
      units,
      tenants,
      // Staged edits included, so the recovery follows the costs on screen
      // rather than the last thing saved. 41001 is derived from the expenses;
      // a cost edited and not yet saved that didn't reach it would make the
      // two halves of the sheet describe different arrangements.
      budget.id === openId && draftExpenseOverrides
        ? draftExpenseOverrides
        : budget.expenseOverrides || {},
    );

  /**
   * The snapshot a publish would write: the whole financial state, assembled
   * in one piece.
   */
  const buildSnapshot = (budget: BudgetYear) => {
    const projection = projectRevenue(
      budget.year,
      units,
      tenants,
      unitLabel,
      configuration,
      taxRolls,
      sales,
      // The budget states BH4 182 #2 and any other unit whose occupancy it
      // could not derive; it must govern the recoveries, not CAM alone.
      budget.camOccupancy,
    );
    const camRecovery = camRecoveryFor(budget);
    return buildPublishedSnapshot({
      budget,
      projected: projection.lines,
      camRecovery,
      units,
      tenants,
      // Only the rates printed above a statement's table are not from the
      // budget — area and what a suite is assessed for are facts about the
      // premises. The same helper the preview uses, so a published statement's
      // rates are the ones that were on screen beforehand.
      ratesFor: (tenant) => statementRatesFor(tenant, unitById, taxRolls, budget.year),
      // The per-unit figures, frozen with the rest: a space that changes hands
      // AFTER publication is divided from these, so a handover never means
      // republishing a year that is meant to stand still.
      basis: statementBasis({ budget, projection, camRecovery, units, tenants }),
      leaseRentFor: (tenant) => {
        const live = tenantExpenseYear({
          tenant,
          year: budget.year,
          unitById,
          configuration,
          taxRolls,
          sales: [],
        });
        const of = (key: string): number[] =>
          (live.lines.find((line) => line.key === key)?.months || new Array(12).fill(0)).map(
            (amount) => amount ?? 0,
          );
        return { baseRent: of("baseRent"), storageRent: of("storageRent") };
      },
      actor,
    });
  };

  /**
   * Builds the snapshot and opens the confirmation on it.
   *
   * Built BEFORE the dialog rather than on confirm, so the figures the admin
   * is shown are the figures that will be written, not a second computation
   * that might differ by a cent or a timestamp. It also means a budget that
   * cannot be published says so before anybody commits to it.
   */
  const openPublish = (budget: BudgetYear) => {
    setPublishError(null);
    setPublishMode("publish");
    try {
      setPending(buildSnapshot(budget));
    } catch (err) {
      setPending(null);
      setPublishError(
        err instanceof PublishIncompleteError
          ? err.message
          : "Couldn't work out the figures to publish.",
      );
    }
  };

  /**
   * Publishing: the complete snapshot and the step tick, in ONE write.
   *
   * Atomic by construction. The snapshot was assembled before the dialog
   * opened and the document is written once, so there is no window in which a
   * budget is locked with half its figures frozen — and a failed or conflicted
   * write leaves the previous published snapshot exactly as it was, which is
   * the safeguard against losing the last finalised figures.
   *
   * Nothing is cleared first, for the same reason.
   */
  const publish = async (budget: BudgetYear) => {
    if (!pending) return;
    setPublishBusy(true);
    setPublishError(null);
    try {
      await saveBudget({
        ...budget,
        publishedSnapshot: pending,
        history: setBudgetStep(budget.history, "published", true, actor),
      });
      setPublishMode(null);
      setPending(null);
    } catch (err) {
      setPublishError(
        err instanceof Error
          ? err.message
          : "Couldn't publish the budget. Nothing was changed — try again.",
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const unlock = async (budget: BudgetYear, reason: string) => {
    setPublishBusy(true);
    setPublishError(null);
    try {
      await unlockBudget(budget.id, reason, actor.byName);
      setPublishMode(null);
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : "Couldn't unlock the budget.");
    } finally {
      setPublishBusy(false);
    }
  };

  // Publishing is not a tick, so neither route freezes anything here: the
  // snapshot is captured by `publish` above, in the same write that sets the
  // step. The history modal refuses the Published row in both directions for
  // the same reason — a step ticked by hand would lock a budget with nothing
  // frozen, and un-ticked by hand would reopen one with no reason recorded.
  const toggleStep = (key: BudgetHistoryStepKey, done: boolean) => {
    if (!historyBudget || key === "published") return;
    patch(historyBudget.id, {
      history: setBudgetStep(historyBudget.history, key, done, actor),
    });
  };

  const advance = (id: string, key: BudgetHistoryStepKey) => {
    const budget = budgets.find((b) => b.id === id);
    if (!budget || key === "published") return;
    patch(id, { history: setBudgetStep(budget.history, key, true, actor) });
  };
  // The open budget's 41001 line. Memoised, because it divides eleven
  // categories across twelve months and ninety-odd units — the Overview
  // animation was once broken by exactly this sort of per-render
  // recomputation, so measure before loosening it.
  const camRecovery = React.useMemo(
    () => (openBudget ? camRecoveryFor(openBudget) : undefined),
    // draftExpenseOverrides is in here deliberately: an expense cell typed and
    // not yet saved has to move the recovery straight away, or the two halves
    // of the sheet disagree until Save Changes is pressed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openBudget, units, tenants, draftExpenseOverrides],
  );

  // THE CAM OCCUPANCY PANEL IS GONE. It listed the units that changed
  // hands and let an admin state who the budget assumed was paying — but
  // what it showed was the year as it turned out, which is the year-end
  // reconciliation's business and not the budget's. A budget charges the
  // tenancy that held a unit when the year opened, and says so once.
  //
  // `camOccupancy` on the budget is UNCHANGED and still honoured: BH4 182 #2
  // is stated against The Greekery for 2025 and the whole budget reads it.
  // There is simply no longer a screen for editing it, which was a one-off
  // for a new build whose lease predated the year.

  /** The management fee as it stands — 55001, derived the same way. */
  const feeLine = React.useMemo(
    () => (openBudget ? managementFeeLine(openBudget.lines, openBudget.splits) : null),
    [openBudget],
  );

  /**
   * The management fee and the CAM recovery, as twelve tab-separated months
   * for pasting straight onto the QuickBooks budget sheet.
   *
   * Cents rather than the display's rounded dollars: this is a figure being
   * keyed into another system, and a budget that disagrees with QuickBooks by
   * a few dollars a line is a reconciliation nobody can close.
   */
  const copyForQbo = () => {
    if (!openBudget) return;
    const fee = feeLine;
    const rows: string[][] = [["Account", ...MONTH_LABELS, "Total"]];
    const add = (label: string, months: number[]) => {
      const twelve = months.length === 12 ? months : new Array(12).fill(0);
      rows.push([
        label,
        ...twelve.map((amount) => amount.toFixed(2)),
        twelve.reduce((a, b) => a + b, 0).toFixed(2),
      ]);
    };

    if (fee) add(MANAGEMENT_FEE_ACCOUNT, fee.months || []);
    if (camRecovery) add(CAM_RECOVERY_ACCOUNT, camRecovery.months);

    navigator.clipboard
      .writeText(rows.map((row) => row.join("\t")).join("\n"))
      .then(() => {
        setQboCopied(true);
        window.setTimeout(() => setQboCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const id = deleting.id;
    deleteBudget(id)
      .then(() => {
        if (openId === id) setOpenId(null);
        setDeleting(null);
      })
      .catch(() => undefined);
  };

  // Finishing the wizard is BOTH the import and the categorising — it won't
  // finish until every expense line has a category, so both steps are recorded
  // together and neither needs a button of its own. Cancelling records nothing,
  // so the budget can't advance past something that didn't happen.
  const saveSetup = (setup: BudgetSetup) => {
    if (!openBudget) return;
    // An import replaces every line. Edits whose account has gone, or whose
    // code now carries a different name, go with it rather than being applied
    // to something the admin never edited — see prunedExpenseEdits.
    const kept = prunedExpenseEdits(
      openBudget.lines,
      setup.lines,
      openBudget.expenseOverrides,
    );
    patch(openBudget.id, {
      ...setup,
      expenseOverrides: kept,
      history: setBudgetStep(openBudget.history, "categoriesAssigned", true, actor),
    });
    setDraftExpenseOverrides(null);
    setSetupOpen(false);
  };

  // Re-categorising a line here does exactly what the wizard does on Finish:
  // the splits are reconciled so a newly used category picks up its configured
  // split, and one no longer used by any line is dropped. Without this, moving
  // the last line off a category would leave its split orphaned on the budget.
  const setLineCategory = (lineId: string, camCategory: string) => {
    if (!openBudget) return;
    const nextLines = openBudget.lines.map((line) =>
      line.id === lineId ? { ...line, camCategory } : line,
    );
    patch(openBudget.id, {
      lines: nextLines,
      splits: resolveCategorySplits(nextLines, openBudget.splits, configuration.camCategories),
    });
  };

  /**
   * A manual adjustment to a projected revenue month, or `null` to clear it.
   *
   * Stored on the budget, not on the lease: it is what this budget expects,
   * which is a different claim from what the tenancy obliges. Cleared keys are
   * removed rather than set to the projected figure, so "no adjustment" and
   * "adjusted to exactly what was projected" can't be confused — and so a later
   * change to the lease moves the cell again, as it should.
   *
   * STAGED, not saved: edits collect in `draftOverrides` and land on the budget
   * when Save Changes is pressed. A budget is read and adjusted in one pass —
   * saving each cell as it was left would write the whole document a dozen
   * times over a single sitting, and would leave no way to think better of it.
   */
  const stage = (
    saved: Record<string, number>,
    prev: Record<string, number> | null,
    key: string,
    amount: number | null,
  ): Record<string, number> | null => {
    const next = { ...(prev ?? saved) };
    if (amount === null) delete next[key];
    else next[key] = amount;

    // STAGED MEANS DIFFERENT FROM SAVED, and nothing else.
    //
    // Comparing against the previous staging was not enough: anything that
    // stages the state it already had — a blur on an untouched cell, a clear
    // where there was nothing to clear, a figure retyped as it was — left a
    // draft that merely EXISTED, and an existing draft lit the button. So the
    // test is against what is on the budget: match it, and there is nothing
    // to save, whatever route got us here.
    const sameAsSaved =
      Object.keys(next).length === Object.keys(saved).length &&
      Object.keys(next).every((k) => saved[k] === next[k]);
    return sameAsSaved ? null : next;
  };

  const setRevenueOverride = (key: string, amount: number | null) => {
    if (!openBudget) return;
    setDraftOverrides((prev) => stage(openBudget.revenueOverrides ?? {}, prev, key, amount));
  };

  /**
   * An adjustment to who carries an expense.
   *
   * Unlike a revenue adjustment this does not change the line — a QuickBooks
   * figure is not TOSP's to restate. It changes the DIVISION: the typed figure
   * stands and the remainder spreads across the tenancies not typed into, so
   * the line still bills out in full. See `allocateLineMonths`.
   */
  const setExpenseOverride = (key: string, amount: number | null) => {
    if (!openBudget) return;
    setDraftExpenseOverrides((prev) =>
      stage(openBudget.expenseOverrides ?? {}, prev, key, amount),
    );
  };

  /** What the panel shows: the staged edits if there are any, else what's saved. */
  const shownOverrides = draftOverrides ?? openBudget?.revenueOverrides ?? {};
  const shownExpenseOverrides =
    draftExpenseOverrides ?? openBudget?.expenseOverrides ?? {};
  /** Reviewed means finished: the figures are frozen and nothing more is typed. */
  const isLocked = !!openBudget?.history?.published;
  const hasUnsavedEdits =
    (draftOverrides !== null || draftExpenseOverrides !== null) && !isLocked;

  // Both halves in one write: a budget is one document, and saving the revenue
  // adjustments while leaving the expense ones staged would make the button a
  // half-truth.
  const saveRevenueEdits = () => {
    if (!openBudget) return;
    patch(openBudget.id, {
      ...(draftOverrides ? { revenueOverrides: draftOverrides } : {}),
      ...(draftExpenseOverrides ? { expenseOverrides: draftExpenseOverrides } : {}),
    });
    setDraftOverrides(null);
    setDraftExpenseOverrides(null);
  };

  // Only expenses are shown — income is imported for the record, and the
  // revenue budget above is where revenue actually comes from.
  // THE WHOLE LINE SET, income included, and the panel hides income itself.
  //
  // It used to be filtered here, which quietly broke the management fee: the
  // panel derives the fee from the lines it is given, and the fee is 4% of the
  // income AND the CAM expenses. Handed expenses only, it charged 4/96 of
  // $918,215 and displayed $38,258.96 — while the 41001 recovery, built from
  // the full set, used the correct $389,006.47. Two different fees on one
  // screen. The filtering belongs where the display is, not where the
  // arithmetic reads from.
  const lines = openBudget?.lines || [];
  const costedLines = lines.filter((line) => line.section !== "income" && !line.isHeader);

  const action = nextBudgetAction(openBudget?.history);

  // ── The budget being worked in ───────────────────────────────────────────
  if (openBudget) {
    return (
      <section className="tosp-card">
        {/* One action at a time, and only while there is one — the button is
            the budget's next step, so it goes away once the round trip is
            done rather than inviting a repeat. Back is parked on the right,
            out of the way of the work. */}
        <div className="tosp-toolbar tosp-toolbar-sticky">
          {action && (
            <button
              className="tosp-btn-next"
              onClick={() => {
                // Publishing is the one action that is not a tick: it captures
                // the whole snapshot and locks the budget, so it goes through
                // its own confirmation.
                if (action.publishes) {
                  setPublishError(null);
                  openPublish(openBudget);
                } else if (action.opensImport) setSetupOpen(true);
                else advance(openBudget.id, action.step);
              }}
            >
              {action.label}
            </button>
          )}
          {/* The two figures QuickBooks hasn't got. Revenue was keyed in at
              step 2, before the CAM expenses arrived, so the fee and the
              recovery it produces are the only lines still to go back —
              offered as a paste rather than a re-export of the whole sheet. */}
          {camRecovery && (
            <button
              className="tosp-btn-secondary"
              title="Copy 55001 Mgmt Fee and 41001 CAMs as twelve months, to paste into QuickBooks"
              onClick={copyForQbo}
            >
              {qboCopied ? "Copied" : "Copy for QBO"}
            </button>
          )}
          {/* PUBLISHING IS THE ACTION BUTTON, not a second one beside it. The
              budget sits at "Review budget" while it is being worked through,
              and the one button at the head of the screen says Publish Budget
              — press it and the year is done. All that is left here is the way
              back out of it, which records a reason. */}
          {isLocked && (
            <button
              className="tosp-btn-secondary"
              title="Reopen this budget for editing. A reason is required."
              onClick={() => {
                setPublishError(null);
                setPublishMode("unlock");
              }}
            >
              Unlock Budget
            </button>
          )}
          {/* Beside Back, and only worth pressing once something has been
              typed — yellow while there are edits to save, quiet otherwise,
              so the state of the screen is readable from the button alone. */}
          <button
            className={`tosp-btn-save-changes tosp-toolbar-end${
              hasUnsavedEdits ? " tosp-btn-save-changes-on" : ""
            }`}
            disabled={!hasUnsavedEdits}
            title={
              hasUnsavedEdits
                ? "Save the adjustments made to this budget"
                : "No changes to save"
            }
            onClick={saveRevenueEdits}
          >
            Save Changes
          </button>
          <button className="tosp-btn-primary" onClick={() => setOpenId(null)}>
            Back
          </button>
        </div>

        {/* Step 1 — revenue, projected from what TOSP already knows. It stands
            on its own: the expense side only arrives once the admin has keyed
            these figures into QuickBooks and exported the result. */}
        <RevenueBudgetPanel
          year={openBudget.year}
          overrides={shownOverrides}
          onOverride={setRevenueOverride}
          // ONLY WHILE PUBLISHED. The snapshot is kept on an unlocked budget
          // as the record of what was posted, but it stops being what the
          // screen reads — otherwise unlocking would free the cells while
          // leaving every line frozen, so a correction made afterwards would
          // appear to do nothing at all.
          snapshot={
            isLocked && openBudget.publishedSnapshot
              ? {
                  at: openBudget.publishedSnapshot.at,
                  by: openBudget.publishedSnapshot.by,
                  byName: openBudget.publishedSnapshot.byName,
                  lines: openBudget.publishedSnapshot.revenue,
                }
              : undefined
          }
          camRecovery={camRecovery}
          locked={isLocked}
          assume={openBudget.camOccupancy}
        />

        {costedLines.length > 0 && (
          <ExpenseBudgetPanel
            year={openBudget.year}
            lines={lines}
            splits={openBudget.splits}
            camCategories={configuration.camCategories}
            overrides={shownExpenseOverrides}
            frozen={isLocked ? openBudget.publishedSnapshot?.expense : undefined}
            frozenTenancies={openBudget.publishedSnapshot?.tenancies}
            assume={openBudget.camOccupancy}
            onOverride={isLocked ? undefined : setExpenseOverride}
            // A reviewed budget's expense side is frozen with the rest of it:
            // re-categorising a line would re-divide costs that were signed off,
            // and re-importing would replace them outright.
            onCategoryChange={isLocked ? undefined : setLineCategory}
            onReimport={isLocked ? undefined : () => setSetupOpen(true)}
          />
        )}

        <BudgetSetupWizard
          isOpen={setupOpen}
          year={openBudget.year}
          initial={{ lines: openBudget.lines, splits: openBudget.splits }}
          camCategories={configuration.camCategories}
          onCancel={() => setSetupOpen(false)}
          onFinish={saveSetup}
        />

        <BudgetPublishModal
          mode={publishMode}
          label={budgetLabel(openBudget)}
          // Read off the snapshot itself, so what is confirmed is what is
          // written rather than a second tally of it.
          summary={
            pending
              ? {
                  revenue: pending.revenue.reduce((sum, line) => sum + line.total, 0),
                  expenses: pending.expense.reduce(
                    (sum, line) => sum + line.months.reduce((a, b) => a + b, 0),
                    0,
                  ),
                  recovered:
                    pending.revenue.find((line) => line.key === CAM_RECOVERY_KEY)?.total ?? 0,
                  statements: pending.statements.length,
                }
              : undefined
          }
          publishedAt={openBudget.publishedSnapshot?.at}
          busy={publishBusy}
          error={publishError}
          onCancel={() => setPublishMode(null)}
          onPublish={() => publish(openBudget)}
          onUnlock={(reason) => unlock(openBudget, reason)}
        />
      </section>
    );
  }

  // ── The list ─────────────────────────────────────────────────────────────
  return (
    <section className="tosp-card">
      <div className="tosp-toolbar">
        <button className="tosp-btn-primary" onClick={() => setAddOpen(true)}>
          + Add Budget
        </button>
      </div>

      <div className="tosp-grid-header" style={{ gridTemplateColumns: LIST_COLUMNS }}>
        {SORT_COLUMNS.map(({ col, label }) => (
          <div key={col}>{sortButton(col, label)}</div>
        ))}
        <div>Actions</div>
      </div>

      {broadcastBlocked && (
        <div className="tosp-banner tosp-banner-warn">
          <span>{broadcastBlocked}</span>
          <button
            type="button"
            className="tosp-btn-secondary"
            onClick={() => setBroadcastBlocked(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {budgets.length === 0 && (
        <div className="tosp-grid-empty">
          No budgets yet. Click "+ Add Budget" to build your first one.
        </div>
      )}

      {sortRows(budgets).map((budget) => (
        <div key={budget.id} className="tosp-grid-row" style={{ gridTemplateColumns: LIST_COLUMNS }}>
          <div className="tosp-cell-wrap">{budgetLabel(budget)}</div>
          <div>{budget.year}</div>
          {/* PUBLISHED AND BROADCAST ARE DIFFERENT THINGS and both are worth
              seeing at a glance: one says the figures are frozen, the other
              says tenants can read them. A budget can be published and not
              broadcast, which is the ordinary state between finishing a year
              and releasing it. Neither replaces the status, which still names
              what to do next while there is anything left to do. */}
          <div className="tosp-budget-flags">
            {budget.history?.published ? (
              <span className="tosp-lease-flag tosp-lease-ok tosp-budget-flag">Published</span>
            ) : (
              <span className="tosp-status-pill">{deriveBudgetStatus(budget.history)}</span>
            )}
            {budget.broadcastExpenses && (
              <span className="tosp-lease-flag tosp-lease-expiring tosp-budget-flag">
                Broadcast
              </span>
            )}
          </div>
          <div className="tosp-actions">
            <button
              className="tosp-btn-icon tosp-btn-view"
              title="Open"
              onClick={() => setOpenId(budget.id)}
            >
              👁
            </button>
            {/* The rest of the row's actions, revealed in place rather than
                dropped over the screen — the SMRT Tasks pattern. */}
            <button
              className="tosp-btn-icon tosp-btn-menu-trigger"
              title={menuFor === budget.id ? "Hide actions" : "More actions"}
              aria-haspopup="true"
              aria-expanded={menuFor === budget.id}
              onClick={() => setMenuFor((open) => (open === budget.id ? null : budget.id))}
            >
              ⋮
            </button>
            {menuFor === budget.id && (
              <>
                <button
                  className="tosp-btn-icon tosp-btn-edit"
                  title="Rename or change the year"
                  onClick={() => setEditing(budget)}
                >
                  ✎
                </button>
                <button
                  className="tosp-btn-icon tosp-btn-delete"
                  title={deleteBlocked(budget) || "Delete"}
                  onClick={() => setDeleting(budget)}
                >
                  🗑
                </button>
                <button
                  className="tosp-btn-icon tosp-btn-history"
                  title="Status history"
                  onClick={() => setHistoryFor(budget.id)}
                >
                  ↻
                </button>
                {/* Amber once locked, the way a sensitive field reads on a
                    profile template. Publishing is what locks a budget, and
                    that is done inside it — so unlocking is all this offers. */}
                <button
                  className={`tosp-btn-icon tosp-btn-lock${
                    budget.history?.published ? " tosp-btn-lock-on" : ""
                  }`}
                  disabled={!budget.history?.published}
                  title={
                    budget.history?.published
                      ? "Locked. Unlock to edit — a reason is required."
                      : "Not published. Publish from inside the budget to lock it."
                  }
                  onClick={() => {
                    setPublishError(null);
                    setOpenId(budget.id);
                    setPublishMode("unlock");
                  }}
                >
                  🔒
                </button>
                <button
                  className={`tosp-btn-icon tosp-btn-broadcast${
                    budget.broadcastExpenses ? " tosp-btn-broadcast-on" : ""
                  }`}
                  title={
                    budget.broadcastExpenses
                      ? "Broadcasting to tenants. Click to stop."
                      : "Broadcast this year to tenants"
                  }
                  onClick={() =>
                    budget.broadcastExpenses
                      ? setBroadcast(budget, false)
                      : setBroadcasting(budget)
                  }
                >
                  📡
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      <AddBudgetModal
        isOpen={addOpen || !!editing}
        existingYears={years}
        // The budget being edited is left out, so keeping its own name isn't a clash.
        existingNames={budgets
          .filter((b) => b.id !== editing?.id)
          .map(budgetLabel)}
        initial={editing ? { year: editing.year, name: budgetLabel(editing) } : null}
        onClose={() => {
          setAddOpen(false);
          setEditing(null);
        }}
        onConfirm={addOrRenameBudget}
      />

      <BudgetHistoryModal
        isOpen={!!historyBudget}
        budgetLabel={historyBudget ? budgetLabel(historyBudget) : ""}
        history={historyBudget?.history}
        onToggleStep={toggleStep}
        onClose={() => setHistoryFor(null)}
      />

      {/* Broadcast puts a year in front of every tenant who holds a statement
          on it, so switching it ON is confirmed. Switching it off is not:
          taking something back is not the direction that needs a guard. */}
      {broadcasting && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: 460 }} role="dialog" aria-modal="true">
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                Broadcast {budgetLabel(broadcasting)}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ marginTop: 0 }}>
                Broadcasting this budget will make it visible to all applicable
                tenants in their Expenses tab. Do you wish to continue?
              </p>
              {!broadcasting.history?.published && (
                <p className="tosp-field-hint">
                  It is not published yet, so tenants will see nothing until it is
                  — but it will appear the moment you publish.
                </p>
              )}
            </div>
            <div className="smrtdocs-modal-footer">
              <button className="tosp-btn-secondary" onClick={() => setBroadcasting(null)}>
                Cancel
              </button>
              <button
                className="tosp-btn-next"
                onClick={() => setBroadcast(broadcasting, true)}
              >
                Broadcast
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deleting && !deleteBlocked(deleting)}
        title="Delete Budget"
        message={
          deleting
            ? `Are you sure you want to delete ${budgetLabel(deleting)}? Its uploaded lines and category splits will be lost. This cannot be undone.`
            : ""
        }
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />

      {/* A budget that may not be deleted says why instead of offering the
          confirmation — the admin has something to do first, and a Delete
          button that refuses after the fact reads as a fault. */}
      {deleting && deleteBlocked(deleting) && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: 460 }} role="dialog" aria-modal="true">
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                Can&apos;t delete {budgetLabel(deleting)}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ marginTop: 0 }}>{deleteBlocked(deleting)}</p>
            </div>
            <div className="smrtdocs-modal-footer">
              <button className="tosp-btn-secondary" onClick={() => setDeleting(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
